import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/zera/Navbar';
import PageHero from '@/components/zera/PageHero';
import Footer from '@/components/zera/Footer';
import { Gem, Shield, Clock, Users, ArrowRight, Compass, Wine, Anchor } from 'lucide-react';

const luxuryExperiences = [
  {
    title: 'Private Yacht Charters',
    description: 'Cruise the Albanian Riviera aboard a luxury vessel, discovering hidden coves and pristine beaches accessible only by sea.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237647164_03f0a4b0.png',
    alt: 'Private luxury yacht charter cruising the Albanian Riviera — hidden coves, Ksamil and Ionian coastline',
    icon: Anchor,
    link: '/yacht-charters',
  },
  {
    title: 'Exclusive Villa Retreats',
    description: 'Handpicked private villas with dedicated staff, infinity pools, and breathtaking views of the Ionian coastline.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237744873_389b5d0f.png',
    alt: 'Exclusive Albania luxury villa rental with infinity pool and Ionian Sea view — private staff and bespoke service',
    icon: Gem,
    link: '/villa-rentals',
  },
  {
    title: 'Culinary Journeys',
    description: 'Private dining experiences with Albania\'s finest chefs, from seaside feasts to mountain farm-to-table dinners under the stars.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237976783_ca13e78b.jpg',
    alt: 'Private Albanian culinary journey — farm-to-table Mediterranean dining, wine tasting and traditional Albania cuisine',
    icon: Wine,
    link: '/booking',
  },
  {
    title: 'Heritage Expeditions',
    description: 'Private guided tours of UNESCO sites with expert archaeologists and historians, unlocking stories hidden for millennia.',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775240919947_f73f6d13.jpg',
    alt: 'Private UNESCO heritage expedition in Albania — Butrint, Berat and Gjirokastër archaeological and Ottoman sites',
    icon: Compass,
    link: '/albanian-travel',
  },
];

const pillars = [
  { icon: Shield, title: 'Uncompromising Privacy', desc: 'Every detail arranged to ensure your complete seclusion and peace of mind.' },
  { icon: Clock, title: 'Bespoke Itineraries', desc: 'No two journeys are alike. Each experience is crafted to your unique vision.' },
  { icon: Users, title: 'Dedicated Concierge', desc: 'A personal concierge available 24/7 to anticipate and fulfill your every need.' },
  { icon: Gem, title: 'Curated Excellence', desc: 'Only the finest accommodations, experiences, and partners meet our standards.' },
];

const LuxuryTravel: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <Navbar />

      <PageHero
        image="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237744873_389b5d0f.png"
        title={"The Art of\nLuxury Travel"}
        subtitle="Where extraordinary places meet extraordinary care — bespoke journeys crafted for the most discerning travelers."
        badge="Luxury Collection"
        height="h-screen"
        imageAlt="Exclusive luxury villa retreat with infinity pool overlooking the Albanian Riviera — bespoke luxury Albania travel"
      />

      {/* Philosophy Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-12 h-[1px] bg-[#C9A961]" />
              <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Our Philosophy</span>
              <div className="w-12 h-[1px] bg-[#C9A961]" />
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] font-light leading-tight mb-8">
              The Art of<br />Transformative Travel
            </h2>
            <p className="font-sans text-sm md:text-base text-[#666] leading-relaxed mb-6">
              At Zera, we believe that travel at its finest is not merely about destinations — it is about the profound transformation that occurs when extraordinary places meet extraordinary care.
            </p>
            <p className="font-sans text-sm md:text-base text-[#666] leading-relaxed mb-6">
              Founded on the principle that luxury should be both deeply personal and environmentally conscious, we curate experiences that honor the natural beauty and cultural richness of every destination we touch.
            </p>
            <p className="font-sans text-sm md:text-base text-[#666] leading-relaxed">
              Our dedicated concierge team works tirelessly to anticipate your every need, crafting bespoke itineraries that reflect your unique vision of the perfect journey.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars of Luxury */}
      <section className="py-20 lg:py-32 bg-[#0D1B2A]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">The Zera Difference</span>
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mt-4">Pillars of Excellence</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar) => (
              <div key={pillar.title} className="text-center p-8 border border-white/10 hover:border-[#C9A961]/30 transition-all duration-500 group">
                {React.createElement(pillar.icon, { className: 'w-8 h-8 text-[#C9A961] mx-auto mb-6 group-hover:scale-110 transition-transform' })}
                <h3 className="font-serif text-lg text-white font-light mb-3">{pillar.title}</h3>
                <p className="font-sans text-xs text-white/50 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Luxury Experiences */}
      <section className="py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Signature Experiences</span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] font-light mt-4">Curated for the Extraordinary</h2>
          </div>

          <div className="space-y-16">
            {luxuryExperiences.map((exp, i) => (
              <div key={exp.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${i % 2 === 1 ? 'lg:direction-rtl' : ''}`}>
                <div className={`relative aspect-[4/3] overflow-hidden ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img
                    src={exp.image}
                    alt={exp.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className={`bg-white p-10 lg:p-16 flex flex-col justify-center ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  {React.createElement(exp.icon, { className: 'w-8 h-8 text-[#C9A961] mb-6' })}
                  <h3 className="font-serif text-2xl md:text-3xl text-[#1A3B5C] font-light mb-4">{exp.title}</h3>
                  <p className="font-sans text-sm text-[#666] leading-relaxed mb-8">{exp.description}</p>
                  <button
                    onClick={() => navigate(exp.link)}
                    className="self-start font-sans text-[10px] tracking-[0.2em] uppercase text-[#1A3B5C] flex items-center gap-2 hover:gap-3 hover:text-[#C9A961] transition-all"
                  >
                    Discover More <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <img
          src="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237667349_afcdbb55.jpg"
          alt="Sunset over the Albanian Riviera — begin your bespoke luxury Albania travel journey with Zera Travel"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-6">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Begin Your Journey</span>
          <h2 className="font-serif text-3xl md:text-5xl text-white font-light mt-4 mb-4">Your Story Awaits</h2>
          <p className="font-sans text-sm text-white/60 max-w-lg mx-auto mb-10">
            Every great journey begins with a single conversation. Let us craft yours.
          </p>
          <button onClick={() => navigate('/booking')} className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all">
            Reserve Your Experience
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LuxuryTravel;
